import { useState } from "react";
import StatusBadge from "@/components/admin/StatusBadge";
import SlotPicker from "@/components/admin/SlotPicker";
import { MODALITY_LABEL, PAYMENT_LABEL } from "@/components/admin/api";
import { formatDateLong } from "@/pages/Agendar";

const BookingCard = ({ b, onAction, acting = false, onOpenPackage }) => {
  const [mode, setMode] = useState(null);
  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  const btn = "rounded-full border px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50";
  const primary = `${btn} border-raiz-800 bg-raiz-800 text-paper hover:bg-raiz-700`;
  const neutral = `${btn} border-raiz-800/25 text-raiz-800 hover:border-raiz-800`;
  const danger = `${btn} border-red-500/40 text-red-700 hover:bg-red-50`;

  const canConfirm = ["aguardando_pagamento", "agendada"].includes(b.status);
  const canRealize = ["agendada", "confirmada", "remarcada"].includes(b.status);
  const canFalta = ["agendada", "confirmada", "remarcada"].includes(b.status);
  const canRemarcar = ["agendada", "confirmada", "remarcada"].includes(b.status) || (b.status === "falta" && b.admin_decision === "mantida");
  const canCancel = ["aguardando_pagamento", "agendada", "confirmada", "remarcada"].includes(b.status);

  return (
    <article data-testid={`booking-card-${b.protocol}`} className="rounded-2xl border border-raiz-900/10 bg-paper p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif text-lg text-ink">
            {b.service_title}
            {b.package_code && b.session_n ? ` — Sessão ${b.session_n}/${b.sessions}` : ""}
          </p>
          <p className="mt-0.5 text-sm text-ink/60">
            {formatDateLong(b.date)} às {b.time} • {MODALITY_LABEL[b.modality]} • {b.plan_name}
          </p>
          {b.package_code && (
            <button
              type="button"
              data-testid={`booking-pacote-${b.protocol}`}
              onClick={() => onOpenPackage?.(b.package_code)}
              className="mt-1.5 text-xs font-medium text-gold-700 underline decoration-gold-400/60 underline-offset-4 hover:text-gold-600"
            >
              Pacote {b.package_code}
            </button>
          )}
        </div>
        <StatusBadge status={b.status} testId={`booking-status-${b.protocol}`} />
      </div>

      <div className="mt-3 text-sm text-ink/70">
        <p className="font-medium text-ink">{b.name}</p>
        <p>
          WhatsApp {b.whatsapp} • {b.email}
        </p>
        <p className="mt-1 text-xs text-ink/50">
          Pagamento: {PAYMENT_LABEL[b.payment_preference]} • Protocolo {b.protocol}
        </p>
        {b.payment?.status === "approved" && (
          <p className="mt-2 inline-flex rounded-full border border-gold-500/50 bg-gold-100/50 px-3 py-1 text-xs font-medium text-gold-700" data-testid={`booking-pago-${b.protocol}`}>
            Pago via Pix{b.payment.paid_at ? ` em ${new Date(b.payment.paid_at).toLocaleDateString("pt-BR")}` : ""}
          </p>
        )}
        {b.payment?.status === "pending" && (
          <p className="mt-2 inline-flex rounded-full border border-amber-500/50 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700" data-testid={`booking-pix-pendente-${b.protocol}`}>
            Pix aguardando pagamento
          </p>
        )}
        {b.remarcada_de && (
          <p className="mt-1 text-xs text-gold-700">
            Remarcada de {formatDateLong(b.remarcada_de.date)} às {b.remarcada_de.time}
          </p>
        )}
        {b.notes && (
          <p className="mt-2 rounded-xl bg-cream p-3 text-xs leading-relaxed text-ink/65">
            <span className="font-medium text-ink">Observação do paciente: </span>
            {b.notes}
          </p>
        )}
        {b.status === "falta" && b.admin_decision && (
          <p className="mt-2 rounded-xl bg-orange-50 p-3 text-xs leading-relaxed text-orange-800" data-testid={`booking-decisao-${b.protocol}`}>
            <span className="font-medium">Decisão: </span>
            {b.admin_decision === "descontada" ? "Sessão descontada do pacote" : "Sessão mantida para reposição"}
            {b.admin_note ? ` — ${b.admin_note}` : ""}
          </p>
        )}
        {b.status !== "falta" && b.admin_note && (
          <p className="mt-2 rounded-xl bg-cream p-3 text-xs leading-relaxed text-ink/65">
            <span className="font-medium text-ink">Nota administrativa: </span>
            {b.admin_note}
          </p>
        )}
      </div>

      {(canConfirm || canRealize || canFalta || canRemarcar || canCancel) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {canConfirm && (
            <button type="button" data-testid={`booking-confirmar-${b.protocol}`} onClick={() => onAction(b.protocol, { action: "confirmar" })} disabled={acting} className={primary}>
              Confirmar agendamento
            </button>
          )}
          {canRealize && (
            <button type="button" data-testid={`booking-realizar-${b.protocol}`} onClick={() => onAction(b.protocol, { action: "realizar" })} disabled={acting} className={primary}>
              Marcar como realizada
            </button>
          )}
          {canFalta && (
            <button type="button" data-testid={`booking-falta-${b.protocol}`} onClick={() => { setMode(mode === "falta" ? null : "falta"); setDecision(""); }} disabled={acting} className={neutral}>
              Registrar falta
            </button>
          )}
          {canRemarcar && (
            <button type="button" data-testid={`booking-remarcar-${b.protocol}`} onClick={() => setMode(mode === "remarcar" ? null : "remarcar")} disabled={acting} className={neutral}>
              Remarcar
            </button>
          )}
          {canCancel && (
            <button type="button" data-testid={`booking-cancelar-${b.protocol}`} onClick={() => onAction(b.protocol, { action: "cancelar" })} disabled={acting} className={danger}>
              Cancelar
            </button>
          )}
        </div>
      )}

      {mode === "falta" && (
        <div className="mt-4 rounded-2xl border border-orange-500/30 bg-orange-50/60 p-4" data-testid={`falta-form-${b.protocol}`}>
          <p className="text-sm font-medium text-ink">Decisão sobre a falta</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { id: "descontada", label: "Descontar a sessão do pacote" },
              { id: "mantida", label: "Manter a sessão para reposição" },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                data-testid={`falta-${o.id}-${b.protocol}`}
                onClick={() => setDecision(o.id)}
                className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                  decision === o.id ? "border-raiz-800 bg-raiz-800 text-paper" : "border-raiz-800/25 text-raiz-900 hover:border-raiz-800"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <input
            className="mt-3 w-full rounded-2xl border border-raiz-800/20 bg-paper px-4 py-2.5 text-sm text-ink focus:border-raiz-700 focus:outline-none"
            placeholder="Nota administrativa (opcional)"
            data-testid={`falta-nota-${b.protocol}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={300}
          />
          <button
            type="button"
            data-testid={`falta-confirmar-${b.protocol}`}
            onClick={() => { onAction(b.protocol, { action: "falta", admin_decision: decision, admin_note: note }); setMode(null); setNote(""); }}
            disabled={acting || !decision}
            className={`${primary} mt-3`}
          >
            Confirmar registro
          </button>
        </div>
      )}

      {mode === "remarcar" && (
        <div className="mt-4 rounded-2xl border border-gold-400/40 bg-gold-100/40 p-4" data-testid={`remarcar-form-${b.protocol}`}>
          <p className="text-sm font-medium text-ink">Nova data e horário</p>
          <div className="mt-3">
            <SlotPicker date={newDate} time={newTime} onDate={setNewDate} onTime={setNewTime} testPrefix={`remarcar-${b.protocol}`} />
          </div>
          <input
            className="mt-3 w-full rounded-2xl border border-raiz-800/20 bg-paper px-4 py-2.5 text-sm text-ink focus:border-raiz-700 focus:outline-none"
            placeholder="Motivo (opcional)"
            data-testid={`remarcar-nota-${b.protocol}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={300}
          />
          <button
            type="button"
            data-testid={`remarcar-confirmar-${b.protocol}`}
            onClick={() => { onAction(b.protocol, { action: "remarcar", new_date: newDate, new_time: newTime, admin_note: note }); setMode(null); setNote(""); setNewDate(""); setNewTime(""); }}
            disabled={acting || !newDate || !newTime}
            className={`${primary} mt-3`}
          >
            Confirmar remarcação
          </button>
        </div>
      )}
    </article>
  );
};

export default BookingCard;
